import { z } from 'zod'
import { GENDERS, LOCALES, SUPPORT_KINDS, REPORT_TARGET_TYPES, THEMES } from './domain.ts'

/**
 * Shared validation schemas.
 *
 * These are imported by BOTH the mobile app and the Supabase edge functions so
 * that client-side and server-side validation cannot drift. A rule that exists
 * only on one side is a rule that does not exist.
 *
 * Requirement IDs refer to docs/01-PRD.md §7.
 */

/* ── Primitives ──────────────────────────────────────────────────────── */

/**
 * Mauritanian phone number (FR-AUTH-03).
 *
 * Accepts `+222########`, `00222########`, `222########` or a bare 8-digit
 * local number, with spaces, dots or dashes as separators. Always NORMALISES to
 * E.164 `+222########`, which is what `profiles.phone` stores.
 */
export const phoneSchema = z
  .string()
  .trim()
  .transform(raw => raw.replace(/[\s.\-()]/g, ''))
  .superRefine((v, ctx) => {
    const local = v.replace(/^(\+222|00222|222)/, '')
    if (!/^\d{8}$/.test(local)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Phone must be a Mauritanian number: 8 digits, optionally prefixed with +222.',
      })
    }
  })
  .transform(v => `+222${v.replace(/^(\+222|00222|222)/, '')}`)

/** Email must be unique and syntactically valid (FR-AUTH-04). Stored lowercase. */
export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(1, 'Email is required.')
  .email('Enter a valid email address.')

/** Minimum 8 characters (FR-AUTH-05). Strength is shown in the UI, not enforced. */
export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters.')
  .max(128, 'Password must be at most 128 characters.')

/** 6-digit SMS PIN (FR-AUTH-07/08). */
export const pinSchema = z
  .string()
  .trim()
  .regex(/^\d{6}$/, 'The code is 6 digits.')

export const uuidSchema = z.string().uuid()

export const localeSchema = z.enum(LOCALES)
export const themeSchema = z.enum(THEMES)
export const genderSchema = z.enum(GENDERS)

/* ── Auth ────────────────────────────────────────────────────────────── */

/** FR-AUTH-01, FR-AUTH-02, FR-AUTH-15. */
export const signupSchema = z.object({
  fullName: z.string().trim().min(2, 'Enter your full name.').max(120),
  email: emailSchema,
  phone: phoneSchema,
  programId: uuidSchema,
  streamId: uuidSchema,
  cityId: uuidSchema,
  gender: genderSchema,
  password: passwordSchema,
  acceptedTerms: z.literal(true, {
    errorMap: () => ({ message: 'You must accept the Terms of Use and Privacy Policy.' }),
  }),
  locale: localeSchema.optional(),
})

/**
 * Login with email OR phone, plus password (FR-AUTH-06).
 * `identifier` is left as typed; the caller resolves which one it is with
 * `resolveIdentifier` below.
 */
export const loginSchema = z.object({
  identifier: z.string().trim().min(1, 'Enter your email or phone number.'),
  password: z.string().min(1, 'Enter your password.'),
})

/** Narrow a login identifier to an email or a normalised E.164 phone. */
export function resolveIdentifier(
  identifier: string,
): { kind: 'email'; value: string } | { kind: 'phone'; value: string } | null {
  const asEmail = emailSchema.safeParse(identifier)
  if (asEmail.success) return { kind: 'email', value: asEmail.data }
  const asPhone = phoneSchema.safeParse(identifier)
  if (asPhone.success) return { kind: 'phone', value: asPhone.data }
  return null
}

/** FR-AUTH-07 — request a PIN by SMS. Rate limiting is server-side (FR-AUTH-09). */
export const otpRequestSchema = z.object({
  phone: phoneSchema,
  purpose: z.enum(['password_reset', 'phone_verification']).default('password_reset'),
})

/** FR-AUTH-08 — verify the PIN. */
export const otpVerifySchema = z.object({
  phone: phoneSchema,
  pin: pinSchema,
  purpose: z.enum(['password_reset', 'phone_verification']).default('password_reset'),
})

/** FR-AUTH-10 — set a new password using a verified reset token. */
export const resetPasswordSchema = z
  .object({
    phone: phoneSchema,
    pin: pinSchema,
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine(v => v.password === v.confirmPassword, {
    message: 'The two passwords do not match.',
    path: ['confirmPassword'],
  })

/* ── Profile ─────────────────────────────────────────────────────────── */

/** FR-PRF-02, FR-PRF-04. Email and phone changes go through verification (FR-PRF-03). */
export const profileUpdateSchema = z
  .object({
    fullName: z.string().trim().min(2).max(120).optional(),
    cityId: uuidSchema.optional(),
    programId: uuidSchema.optional(),
    streamId: uuidSchema.optional(),
    avatarUrl: z.string().url().nullable().optional(),
    locale: localeSchema.optional(),
    theme: themeSchema.optional(),
  })
  .refine(v => Object.values(v).some(x => x !== undefined), {
    message: 'Nothing to update.',
  })

/** FR-PRF-05. */
export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password.'),
    newPassword: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine(v => v.newPassword === v.confirmPassword, {
    message: 'The two passwords do not match.',
    path: ['confirmPassword'],
  })
  .refine(v => v.newPassword !== v.currentPassword, {
    message: 'The new password must be different from the current one.',
    path: ['newPassword'],
  })

/* ── Community ───────────────────────────────────────────────────────── */

/**
 * FR-COM-01/02/03. Body length mirrors the DB constraint
 * `check (char_length(body) between 10 and 4000)`.
 */
export const postCreateSchema = z.object({
  subjectId: uuidSchema,
  contentId: uuidSchema.nullable().optional(),
  body: z
    .string()
    .trim()
    .min(10, 'A post must be at least 10 characters.')
    .max(4000, 'A post must be at most 4000 characters.'),
  imageUrl: z.string().url().nullable().optional(),
})

/** FR-COM-04. */
export const replyCreateSchema = z.object({
  postId: uuidSchema,
  body: z
    .string()
    .trim()
    .min(1, 'A reply cannot be empty.')
    .max(4000, 'A reply must be at most 4000 characters.'),
})

/** FR-COM-06 — one report per user per item is enforced by a DB unique index. */
export const reportCreateSchema = z.object({
  targetType: z.enum(REPORT_TARGET_TYPES),
  targetId: uuidSchema,
  reason: z
    .string()
    .trim()
    .min(3, 'Tell us why you are reporting this.')
    .max(500, 'Keep the reason under 500 characters.'),
})

/* ── Progress ────────────────────────────────────────────────────────── */

/**
 * FR-PRG-01/06.
 *
 * `clientEventId` is the idempotency key written to
 * `watch_sessions.client_event_id` (unique). A request retried over a flaky
 * connection may arrive more than once; the same id must never be counted twice.
 */
export const progressEventSchema = z.object({
  contentId: uuidSchema,
  /** Resume point, seconds from the start of the video. */
  positionSec: z.number().int().min(0),
  /** De-duplicated watch time accumulated in this event, in seconds. */
  watchedSec: z.number().int().min(0),
  clientEventId: uuidSchema,
  occurredAt: z.coerce.date(),
  deviceId: z.string().max(128).optional(),
})

/** A client may submit several buffered events in one request. */
export const progressBatchSchema = z.object({
  events: z.array(progressEventSchema).min(1).max(200),
})

/* ── Payment ─────────────────────────────────────────────────────────── */

/**
 * FR-SUB-05/06. Provider-agnostic. `mock` is available in non-production
 * builds only; `outcome` drives the deterministic QA success/failure paths.
 */
export const paymentInitiateSchema = z.object({
  planId: uuidSchema,
  provider: z.enum(['mock', 'bankily', 'masrvi', 'sedad']),
  phone: phoneSchema.optional(),
  outcome: z.enum(['success', 'failure']).optional(),
})

/* ── Support ─────────────────────────────────────────────────────────── */

/** FR-SUP-02 — account context is attached server-side, not trusted from the client. */
export const contactSchema = z.object({
  subject: z.string().trim().min(3, 'Add a short subject.').max(140),
  body: z.string().trim().min(10, 'Tell us a little more.').max(4000),
  email: emailSchema.optional(),
  appVersion: z.string().max(32).optional(),
  platform: z.string().max(32).optional(),
})

/** FR-SUP-03. */
export const suggestionSchema = z.object({
  body: z.string().trim().min(10, 'Tell us a little more.').max(4000),
  appVersion: z.string().max(32).optional(),
  platform: z.string().max(32).optional(),
})

/** Discriminator for `support_messages.kind`. */
export const supportKindSchema = z.enum(SUPPORT_KINDS)

/* ── Inferred types ──────────────────────────────────────────────────── */

export type Phone = z.infer<typeof phoneSchema>
export type Email = z.infer<typeof emailSchema>
export type Password = z.infer<typeof passwordSchema>
export type Pin = z.infer<typeof pinSchema>

export type SignupInput = z.input<typeof signupSchema>
export type Signup = z.infer<typeof signupSchema>
export type Login = z.infer<typeof loginSchema>
export type OtpRequest = z.infer<typeof otpRequestSchema>
export type OtpVerify = z.infer<typeof otpVerifySchema>
export type ResetPassword = z.infer<typeof resetPasswordSchema>

export type ProfileUpdate = z.infer<typeof profileUpdateSchema>
export type ChangePassword = z.infer<typeof changePasswordSchema>

export type PostCreate = z.infer<typeof postCreateSchema>
export type ReplyCreate = z.infer<typeof replyCreateSchema>
export type ReportCreate = z.infer<typeof reportCreateSchema>

export type ProgressEventInput = z.input<typeof progressEventSchema>
export type ProgressEvent = z.infer<typeof progressEventSchema>
export type ProgressBatch = z.infer<typeof progressBatchSchema>

export type PaymentInitiate = z.infer<typeof paymentInitiateSchema>
export type Contact = z.infer<typeof contactSchema>
export type Suggestion = z.infer<typeof suggestionSchema>
